import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
  CreateActivimeterDetailDto,
  ActivimeterDetailDto,
  HotLabEquipmentDto,
  HotLabEquipmentType,
} from '@services/dtos'
import { activimeterDetailSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Grid } from '@mui/material'
import {
  createActivimeterDetail,
  updateActivimeterDetail,
} from '@state/reducers/activimeterDetailReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import { AddWrapperInput } from '../inputs/AddWrapperInput'
import { FormCrudHeader } from './FormCrudHeader'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import HotLabEquipmentForm from './HotLabEquipmentForm'
import { hotLabEquipmentService } from '@services/hotLabEquipmentService'
import { CheckBoxInput } from '../inputs'

type Props = {
  editingActivimeterDetail?: ActivimeterDetailDto | null
  onClose: () => void
}

const ActivimeterDetailForm: React.FC<Props> = ({
  editingActivimeterDetail,
  onClose,
}) => {
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<CreateActivimeterDetailDto>({
    defaultValues: editingActivimeterDetail
      ? {
          ...{ ...editingActivimeterDetail, equipment: undefined },
          equipmentId: editingActivimeterDetail.equipment.id,
        }
      : {},
    resolver: yupResolver(activimeterDetailSchema),
  })

  const dispatch = useAppDispatch()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])

  const onSubmitForm = async (data: CreateActivimeterDetailDto) => {
    setIsSubmitting(true)
    const displayErrorMessage = () => cb('Une erreur est survenue')

    if (editingActivimeterDetail) {
      dispatch(
        updateActivimeterDetail({ id: editingActivimeterDetail.id, dto: data }),
      )
        .unwrap()
        .then(() => {
          cb('Le paramètrage activimètre a été modifié avec succès')
          onClose()
        })
        .catch(displayErrorMessage)
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createActivimeterDetail(data))
        .unwrap()
        .then(() => {
          cb('Le paramètrage activimètre a été créé avec succès')
          onClose()
        })
        .catch(displayErrorMessage)
        .finally(() => setIsSubmitting(false))
    }
  }

  const fields = [
    { name: 'path', label: 'Chemin', sm: '12' },
    {
      name: 'electronicZero',
      label: '0 électronique (fA)',
      sm: '4',
      type: 'number',
    },
    { name: 'voltageMin', label: 'Tension min (V)', sm: '4', type: 'number' },
    { name: 'voltageMax', label: 'Tension max (V)', sm: '4', type: 'number' },
    {
      name: 'backgroundWithoutSampleHolderMbq',
      label: 'Fond sans porte-échantillon (MBq)',
      sm: '6',
      type: 'number',
    },
    {
      name: 'backgroundWithSampleHolderMbq',
      label: 'Fond porte-échantillon (MBq)',
      sm: '6',
      type: 'number',
    },
    {
      name: 'testWithCobalt57',
      label: 'Test avec Cobalt 57',
      sm: '4',
      type: 'checkbox',
    },
    {
      name: 'testWithCesium137',
      label: 'Test avec Césium 137',
      sm: '4',
      type: 'checkbox',
    },
    {
      name: 'testWithBarium133',
      label: 'Test avec Baryum 133',
      sm: '4',
      type: 'checkbox',
    },
  ]

  const fetchActivimeters = async (search: string) => {
    const page = await hotLabEquipmentService.getAll({
      search,
      type: HotLabEquipmentType.ACTIVIMETER,
      isConfigurable: true,
    })
    return page?.datas ?? []
  }

  const renderFields = (fields: any[]) =>
    fields.map((field) => (
      <Grid item xs={12} sm={field.sm ?? 6} key={field.name}>
        {field.type === 'checkbox' ? (
          <CheckBoxInput
            control={control}
            name={field.name}
            label={field.label}
          />
        ) : (
          <InputField
            control={control}
            name={field.name}
            label={field.label}
            type={field.type ?? 'text'}
          />
        )}
      </Grid>
    ))

  return (
    <div className="flex w-full h-full p-4">
      <form onSubmit={handleSubmit(onSubmitForm)} className="w-full">
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingActivimeterDetail}
        />

        <div className="max-w-screen-sm mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Paramétrage activimètre
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              <Grid item xs={12}>
                <AddWrapperInput
                  legend="Activimètre"
                  addTooltipTitle="Ajouter un activimètre"
                  dialogContent={(close) => (
                    <HotLabEquipmentForm
                      onClose={close}
                      defaultValues={{ type: HotLabEquipmentType.ACTIVIMETER }}
                    />
                  )}
                  input={
                    <ServerAutocomplete<HotLabEquipmentDto>
                      autoFetchOnMount
                      fetchService={fetchActivimeters}
                      onChange={(value: HotLabEquipmentDto | null) => {
                        setValue('equipmentId', value?.id ?? '')
                      }}
                      getValue={(value) => value?.id?.toString() || null}
                      getOptionLabel={(value) => {
                        const equipment = value as HotLabEquipmentDto
                        return equipment ? equipment.name : ''
                      }}
                      debounceMs={300}
                      placeholder="Rechercher un activimètre"
                      label="Activimètre"
                      helperText={errors.equipmentId?.message}
                      error={!!errors.equipmentId}
                      minSearchLength={0}
                      initialOptions={
                        editingActivimeterDetail?.equipment
                          ? [editingActivimeterDetail.equipment]
                          : []
                      }
                      value={editingActivimeterDetail?.equipment ?? null}
                    />
                  }
                />
              </Grid>

              {renderFields(fields)}
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default ActivimeterDetailForm
